import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppleLogo,
  DeviceMobile,
  FacebookLogo,
  GoogleLogo,
  Key,
  Lock,
  ShieldCheck,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { Icon } from '../../../../../components/Icon';
import { ChangePasswordDialog } from '../../../../../components/settings/change-password-dialog';
import { useReauth } from '../../../../../components/settings/reauth';
import { SettingsRow } from '../../../../../components/settings/settings-section';
import { SignOutOthersDialog } from '../../../../../components/settings/two-factor-dialogs';
import { useAccountSecurity } from '../../../../../components/settings/use-account-security';
import { useTwoFactorControls } from '../../../../../components/settings/use-two-factor-controls';
import { Banner } from '../../../../../components/ui/banner';
import { Button } from '../../../../../components/ui/button';
import { Card } from '../../../../../components/ui/card';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { StatusTag } from '../../../../../components/ui/status-tag';
import { Toggle } from '../../../../../components/ui/toggle';
import { useToast } from '../../../../../components/ui/toast';
import { describeUserAgent, formatActivity, formatChanged } from '../../../../../lib/session-format';
import { sessionQueryOptions } from '../../../../../lib/session';
import { ErrorState } from '../../../../../components/ui/state-views';

export const Route = createFileRoute('/subdomain/app/_client/settings/security')({
  component: SecurityPage,
});

function Group({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card padding="lg" className="flex flex-col gap-(--mbm-space-2)">
      <div className="flex items-center justify-between">
        <h2 className="text-label-large text-text-primary">{title}</h2>
        {action}
      </div>
      <div className="divide-y divide-border-line">{children}</div>
    </Card>
  );
}

function SecurityPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { guard, dialog: reauthDialog } = useReauth({ forgotPasswordTo: '/subdomain/app/forgot-password' });
  const account = useAccountSecurity();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);

  const twoFactorOn = Boolean(account.current?.user.twoFactorEnabled);
  // The same two-step flows as the staff app: one implementation, two Figma layouts.
  const twoFactor = useTwoFactorControls({
    guard,
    audience: 'client',
    onProblem: account.setProblem,
    onChanged: () => void queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey }),
  });

  const { accounts, sessions, credential, link, unlink, revoke, otherDevices } = account;

  const socialRow = (providerId: 'google' | 'facebook', name: string, glyph: PhosphorIcon) => {
    const linked = accounts.data?.find((a) => a.providerId === providerId);
    return (
      <SettingsRow
        icon={<Icon icon={glyph} size="md" />}
        title={name}
        subtitle={linked ? 'Connected' : 'Not connected'}
        trailing={
          linked ? (
            <Button
              variant="secondary"
              size="small"
              loading={unlink.isPending}
              onClick={() => unlink.mutate(linked.id)}
            >
              Disconnect
            </Button>
          ) : (
            <Button variant="secondary" size="small" loading={link.isPending} onClick={() => link.mutate(providerId)}>
              Connect
            </Button>
          )
        }
      />
    );
  };

  return (
    <>
      {account.problem ? <Banner tone="error">{account.problem}</Banner> : null}

      <Group title="Sign-in methods">
        {accounts.isLoading ? (
          <Skeleton className="my-(--mbm-space-3) h-(--mbm-size-list-row-tall) rounded-(--mbm-radius-md)" />
        ) : (
          <>
            {socialRow('google', 'Google', GoogleLogo)}
            <SettingsRow
              icon={<Icon icon={Lock} size="md" />}
              title="Email & password"
              subtitle={credential ? `Last changed ${formatChanged(credential.updatedAt)}` : 'Not set'}
              trailing={
                credential ? (
                  <Button variant="secondary" size="small" onClick={() => setPasswordOpen(true)}>
                    Change
                  </Button>
                ) : undefined
              }
            />
            {socialRow('facebook', 'Facebook', FacebookLogo)}
            <SettingsRow
              icon={<Icon icon={AppleLogo} size="md" />}
              title="Apple"
              subtitle="Not connected"
              trailing={
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => toast({ title: 'Apple sign-in is coming soon', tone: 'info' })}
                >
                  Connect
                </Button>
              }
            />
          </>
        )}
      </Group>

      <Group title="Extra safety">
        <SettingsRow
          icon={<Icon icon={ShieldCheck} size="md" />}
          title="Two-step verification"
          badge={twoFactorOn ? <StatusTag label="On" tone="green" /> : undefined}
          subtitle="A 6-digit code from your authenticator app each time you sign in"
          trailing={
            <Toggle
              aria-label="Two-step verification"
              checked={twoFactorOn}
              onCheckedChange={(on) => (on ? twoFactor.startSetup() : twoFactor.requestTurnOff())}
            />
          }
        />
        {twoFactorOn ? (
          <SettingsRow
            icon={<Icon icon={Key} size="md" />}
            title="Backup codes"
            subtitle="Use them if you lose your phone."
            trailing={
              <Button variant="secondary" size="small" onClick={twoFactor.viewCodes}>
                View codes
              </Button>
            }
          />
        ) : null}
      </Group>

      <Group
        title="Where you’re signed in"
        action={
          otherDevices.length > 0 ? (
            <button
              type="button"
              onClick={() => setSignOutOpen(true)}
              className="rounded-(--mbm-radius-xs) text-label-medium text-feedback-error focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              Sign out of all other devices
            </button>
          ) : undefined
        }
      >
        {sessions.isLoading ? (
          <Skeleton className="my-(--mbm-space-3) h-(--mbm-size-list-row-tall) rounded-(--mbm-radius-md)" />
        ) : sessions.isError ? (
          <ErrorState
            title="Couldn’t load your devices"
            className="py-(--mbm-space-5)"
            onRetry={() => void sessions.refetch()}
          />
        ) : (
          sessions.data?.map((s) => {
            const isThisDevice = s.token === account.current?.session.token;
            return (
              <SettingsRow
                key={s.id}
                icon={<Icon icon={DeviceMobile} size="md" />}
                title={describeUserAgent(s.userAgent)}
                badge={isThisDevice ? <StatusTag label="This device" tone="blue" /> : undefined}
                subtitle={[s.ipAddress, formatActivity(s.updatedAt)].filter(Boolean).join(' · ')}
                trailing={
                  isThisDevice ? undefined : (
                    <Button
                      variant="secondary"
                      size="small"
                      loading={revoke.isPending && revoke.variables === s.token}
                      onClick={() => revoke.mutate(s.token)}
                    >
                      Sign out
                    </Button>
                  )
                }
              />
            );
          })
        )}
      </Group>

      {reauthDialog}
      {twoFactor.dialogs}
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      <SignOutOthersDialog
        open={signOutOpen}
        deviceNames={otherDevices.map((s) => describeUserAgent(s.userAgent))}
        busy={account.revokeOthers.isPending}
        onClose={() => setSignOutOpen(false)}
        onConfirm={() => account.revokeOthers.mutate(undefined, { onSettled: () => setSignOutOpen(false) })}
      />
    </>
  );
}
